/**
 * shared/utils/useUrlState.js — v2.80
 * ─────────────────────────────────────
 * Drop-in replacement for `useState(initial)` that mirrors the value
 * into a URL search-param of the same key. Back/forward restores it.
 *
 * Why a custom hook (not React-Query / nuqs / etc.)?
 *   • No new dependency
 *   • Matches the existing `useLocation` + `useNavigate` flow already used
 *     across Dashboard.jsx (v2.75)
 *   • Coerces the value type from the default (number / boolean / string)
 *     so consumers don't get hit with `"42"` vs `42` surprises
 *   • Omits default / empty values from the URL → clean shareable links
 *
 * Multi-key writes
 * ─────────────────
 * `useUrlStateMulti(defaults)` mirrors a whole bag of keys at once. Use
 * this for pages with many filters (Admin Listings, Dealer Dashboard)
 * to avoid stacking N history entries per chip click.
 *
 * NOTE: every URL write uses `replace: true` so the back button returns
 * to where the user came from, not through every filter toggle.
 */

import { useCallback, useMemo, useState, useEffect, useRef } from "react";
import { useLocation, useNavigate } from "react-router-dom";

function coerceFromDefault(raw, dflt) {
  if (raw == null) return dflt;
  if (typeof dflt === "number") {
    const n = Number(raw);
    return Number.isFinite(n) ? n : dflt;
  }
  if (typeof dflt === "boolean") {
    if (raw === "true" || raw === "1") return true;
    if (raw === "false" || raw === "0") return false;
    return dflt;
  }
  return String(raw);
}

function isDefault(value, dflt) {
  if (value === dflt) return true;
  if (dflt === "" && (value == null || value === "")) return true;
  if (dflt === 0 && (value == null || value === 0)) return true;
  if (dflt === false && (value == null || value === false)) return true;
  return false;
}

/**
 * Single-key URL mirror. Behaves like `useState(initial)` but also writes
 * `?<key>=<value>` to the URL on every change.
 *
 * @param {string} key  URL param key
 * @param {string|number|boolean} initial  Default value (type-inferred)
 * @returns {[value, setValue]}
 */
export function useUrlState(key, initial) {
  const loc = useLocation();
  const nav = useNavigate();
  const [value, _setValue] = useState(() => {
    const params = new URLSearchParams(loc.search);
    return coerceFromDefault(params.get(key), initial);
  });

  // Re-read on URL change (back/forward).
  const lastUrlRef = useRef(loc.search);
  useEffect(() => {
    if (loc.search === lastUrlRef.current) return;
    lastUrlRef.current = loc.search;
    const params = new URLSearchParams(loc.search);
    const next = coerceFromDefault(params.get(key), initial);
    _setValue((cur) => (cur === next ? cur : next));
  }, [loc.search, key]);

  const setValue = useCallback((next) => {
    _setValue((cur) => {
      const resolved = typeof next === "function" ? next(cur) : next;
      // Mirror into URL on the next tick to avoid mid-render navigation.
      queueMicrotask(() => {
        const params = new URLSearchParams(window.location.search);
        if (isDefault(resolved, initial)) params.delete(key);
        else params.set(key, String(resolved));
        const qs = params.toString();
        const target = qs ? `?${qs}` : window.location.pathname;
        // Only navigate if the URL actually changes.
        if (window.location.search !== (qs ? `?${qs}` : "")) {
          nav(target, { replace: true });
        }
      });
      return resolved;
    });
  }, [nav, key, initial]);

  return [value, setValue];
}

/**
 * Multi-key URL mirror. Stores a whole record-shaped state object in the
 * URL. Use this for pages with rich filter sets so a single chip click
 * only writes one history entry (with all current params), not N.
 *
 * Returned `set(key, value)` (and `setAll(patch)`) mirror into the URL
 * with `replace:true` — back button returns to the previous page, not
 * through every filter toggle.
 *
 * @param {Record<string, string|number|boolean>} defaults
 * @returns {{values, set, setAll, reset}}
 */
export function useUrlStateMulti(defaults) {
  const loc = useLocation();
  const nav = useNavigate();
  // Defaults shape is stable across renders if caller wraps in useMemo.
  const defaultsRef = useRef(defaults);
  defaultsRef.current = defaults;

  const readFromUrl = useCallback(() => {
    const params = new URLSearchParams(loc.search);
    const out = {};
    for (const [k, dflt] of Object.entries(defaultsRef.current)) {
      out[k] = coerceFromDefault(params.get(k), dflt);
    }
    return out;
  }, [loc.search]);

  const [values, _setValues] = useState(readFromUrl);

  // Re-read on URL change (back/forward).
  const lastUrlRef = useRef(loc.search);
  useEffect(() => {
    if (loc.search === lastUrlRef.current) return;
    lastUrlRef.current = loc.search;
    _setValues(readFromUrl());
  }, [loc.search, readFromUrl]);

  const writeUrl = useCallback((nextValues) => {
    const params = new URLSearchParams(window.location.search);
    for (const [k, dflt] of Object.entries(defaultsRef.current)) {
      if (isDefault(nextValues[k], dflt)) params.delete(k);
      else params.set(k, String(nextValues[k]));
    }
    const qs = params.toString();
    const cur = window.location.search;
    const target = qs ? `?${qs}` : window.location.pathname;
    if (cur !== (qs ? `?${qs}` : "")) {
      nav(target, { replace: true });
    }
  }, [nav]);

  const set = useCallback((key, value) => {
    _setValues((cur) => {
      const next = { ...cur, [key]: typeof value === "function" ? value(cur[key]) : value };
      queueMicrotask(() => writeUrl(next));
      return next;
    });
  }, [writeUrl]);

  const setAll = useCallback((patch) => {
    _setValues((cur) => {
      const next = { ...cur, ...patch };
      queueMicrotask(() => writeUrl(next));
      return next;
    });
  }, [writeUrl]);

  const reset = useCallback(() => {
    _setValues(() => {
      const next = { ...defaultsRef.current };
      queueMicrotask(() => writeUrl(next));
      return next;
    });
  }, [writeUrl]);

  return useMemo(() => ({ values, set, setAll, reset }), [values, set, setAll, reset]);
}
